import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import API from "../API/Api";

dayjs.extend(customParseFormat);

const STATUS = {
    PENDING: {
        label: "Pickup Pending",
        cls: "bg-yellow-100 text-yellow-700",
        message:
            "Your donation has been received. Our team will arrange the free Loomis pickup soon.",
    },

    ARRANGED: {
        label: "Pickup Scheduled",
        cls: "bg-blue-100 text-blue-700",
        message:
            "Your Loomis pickup has been scheduled.",
    },

    PICKED_UP: {
        label: "Picked Up",
        cls: "bg-green-100 text-green-700",
        message:
            "Your frames have been picked up by Loomis. They are now on their way to ATAL Optical.",
    },

    DELIVERED: {
        label: "Delivered",
        cls: "bg-purple-100 text-purple-700",
        message:
            "Your donated frames have been delivered to ATAL Optical. Thank you for supporting our frame donation initiative!",
    },

    CANCELLED: {
        label: "Cancelled",
        cls: "bg-gray-200 text-gray-700",
        message:
            "This pickup was cancelled. Please contact us if you'd like to reschedule.",
    },
};
const getStatusInfo = (status) => STATUS[status] || STATUS.PENDING;

const fmtDate = (d) =>
    d ? dayjs(d, "YYYYMMDD").format("dddd, MMM D, YYYY") : "N/A";

const fmtTime = (t) =>
    t && String(t).length === 4
        ? dayjs(String(t), "HHmm").format("h:mm A")
        : t || "N/A";

const PickupStatus = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [pickup, setPickup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchStatus = async () => {
        try {
            setLoading(true);
            setError("");
            const res = await API.get(`/donate-frame/pickup/${token}`);
            setPickup(res.data.pickup);
        } catch (err) {
            console.error("Pickup status fetch failed:", err);
            setError(
                err.response?.data?.message ||
                "We couldn't find this pickup. The link may be invalid or expired."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <p className="text-gray-600">Loading your pickup status...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-2xl mx-auto px-5 py-16 text-center">
                <h1 className="text-2xl font-bold text-[#f00000] mb-3">
                    Pickup Not Found
                </h1>
                <p className="text-gray-600 mb-6">{error}</p>
                <a
                    href="mailto:info.ataloptical@gmail.com"
                    className="text-[#f00000] underline font-medium"
                >
                    Contact us for help
                </a>
            </div>
        );
    }

    const status = getStatusInfo(pickup.status);

    const formatLoomisDateTime = (dateTime) => {
        if (!dateTime) return null;

        const value = String(dateTime).trim();

        const match = value.match(
            /^(\d{8})\s+(\d{6})$/
        );

        if (!match) return value;

        const date = match[1];
        const time = match[2];

        const year = Number(date.slice(0, 4));
        const month = Number(date.slice(4, 6)) - 1;
        const day = Number(date.slice(6, 8));

        const hour = Number(time.slice(0, 2));
        const minute = Number(time.slice(2, 4));

        const formatted = new Date(
            year,
            month,
            day,
            hour,
            minute
        );

        return formatted.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const timeline = [
        {
            key: "received",
            title: "Donation Received",
            description:
                "Your frame donation has been submitted successfully.",
            completed: true,
        },
        {
            key: "arranged",
            title: "Pickup Scheduled",
            description:
                pickup.status === "ARRANGED"
                    ? pickup.pickupDate
                        ? `Loomis pickup was scheduled for ${fmtDate(
                            pickup.pickupDate
                        )}.`
                        : "Loomis pickup has been arranged for your donation."
                    : pickup.status === "PICKED_UP" ||
                        pickup.status === "DELIVERED"
                        ? "Loomis pickup was successfully completed."
                        : "Loomis pickup has been arranged for your donation.",
            completed: [
                "ARRANGED",
                "PICKED_UP",
                "DELIVERED",
            ].includes(pickup.status),
        },
        {
            key: "picked_up",
            title: "Picked Up by Loomis",
            description:
                pickup.pickedUpOn
                    ? `Loomis collected your frames on ${formatLoomisDateTime(
                        pickup.pickedUpOn
                    )}.`
                    : "Loomis has collected your donated frames.",
            completed: [
                "PICKED_UP",
                "DELIVERED",
            ].includes(pickup.status),
        },
        {
            key: "delivered",
            title: "Delivered to ATAL Optical",
            description:
                pickup.deliveredOn
                    ? `Your frames were delivered to ATAL Optical on ${formatLoomisDateTime(
                        pickup.deliveredOn
                    )}.`
                    : "Your donated frames have reached ATAL Optical.",
            completed:
                pickup.status === "DELIVERED",
        },
    ];

    return (
        <div className="max-w-2xl mx-auto px-5 py-10">
            <h1 className="text-3xl font-bold text-[#f00000] mb-1">
                Frame Pickup Status
            </h1>
            <p className="text-gray-600 mb-8">
                Hello {pickup.name}, here's the latest on your donation.
            </p>

            <div className="bg-white border rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mt-6">
                    <button
                        onClick={() => navigate("/my-donations")}
                        className="px-4 mb-6 py-2 border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    >
                        ← Back to My Donations
                    </button>

                    <button
                        onClick={fetchStatus}
                        className="px-4 py-2 mb-6 border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    >
                        Refresh
                    </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-5 text-sm">
                    <div>
                        <p className="text-gray-500">Quantity donated</p>
                        <p className="font-semibold">
                            {pickup.frameQuantity ?? "Not recorded"}
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-500">Pickup address</p>
                        <p className="font-semibold">
                            {[pickup.address, pickup.city, pickup.province, pickup.postal]
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                        </p>
                    </div>
                </div>

                <div className="mt-6 border rounded-xl p-6 bg-white">
                    <h3 className="text-lg font-bold mb-6">
                        Donation Journey
                    </h3>

                    <div className="space-y-0">
                        {timeline.map((item, index) => (
                            <div
                                key={item.key}
                                className="flex gap-4"
                            >
                                {/* Timeline indicator */}
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${item.completed
                                            ? "bg-green-500 text-white"
                                            : "bg-gray-200 text-gray-500"
                                            }`}
                                    >
                                        {item.completed ? "✓" : index + 1}
                                    </div>

                                    {index < timeline.length - 1 && (
                                        <div
                                            className={`w-0.5 h-12 ${item.completed
                                                ? "bg-green-500"
                                                : "bg-gray-200"
                                                }`}
                                        />
                                    )}
                                </div>

                                {/* Timeline content */}
                                <div className="pb-6">
                                    <h4
                                        className={`font-semibold ${item.completed
                                            ? "text-gray-900"
                                            : "text-gray-400"
                                            }`}
                                    >
                                        {item.title}
                                    </h4>

                                    <p
                                        className={`text-sm mt-1 ${item.completed
                                            ? "text-gray-600"
                                            : "text-gray-400"
                                            }`}
                                    >
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PickupStatus;